import React, { useRef, useState, useCallback } from "react";

/**
 * ResponsiveCarousel
 * - Desktop (md+): renders items in a CSS grid.
 * - Mobile (<md): renders a horizontal scroll-snap carousel with dot indicators.
 *
 * Props:
 *  items: array
 *  renderItem: (item, index) => node
 *  gridCols: tailwind class for md grid (e.g. "md:grid-cols-3")
 *  cardWidth: mobile card width class (default "w-[82vw]")
 *  testId
 */
export const ResponsiveCarousel = ({
  items,
  renderItem,
  gridCols = "md:grid-cols-3",
  cardWidth = "w-[82vw] sm:w-[60vw]",
  testId = "responsive-carousel",
}) => {
  const trackRef = useRef(null);
  const [active, setActive] = useState(0);

  const onScroll = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const cards = Array.from(el.children);
    const center = el.scrollLeft + el.clientWidth / 2;
    let closest = 0;
    let min = Infinity;
    cards.forEach((c, i) => {
      const cardCenter = c.offsetLeft + c.clientWidth / 2;
      const dist = Math.abs(cardCenter - center);
      if (dist < min) {
        min = dist;
        closest = i;
      }
    });
    setActive(closest);
  }, []);

  const scrollToIndex = (i) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.children[i];
    if (card) {
      el.scrollTo({ left: card.offsetLeft - 16, behavior: "smooth" });
    }
  };

  return (
    <div data-testid={testId}>
      {/* Desktop grid */}
      <div className={`hidden md:grid ${gridCols} gap-6`}>
        {items.map((item, i) => (
          <div key={i} className="h-full">
            {renderItem(item, i)}
          </div>
        ))}
      </div>

      {/* Mobile carousel */}
      <div className="md:hidden">
        <div
          ref={trackRef}
          onScroll={onScroll}
          data-testid={`${testId}-track`}
          className="flex flex-nowrap overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-4 -mx-4 px-4 pb-2"
        >
          {items.map((item, i) => (
            <div
              key={i}
              className={`shrink-0 ${cardWidth} snap-center`}
            >
              {renderItem(item, i)}
            </div>
          ))}
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mt-5" data-testid={`${testId}-dots`}>
          {items.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`${testId}-dot-${i}`}
              onClick={() => scrollToIndex(i)}
              className={`h-2 rounded-full transition-all duration-300 ${
                active === i ? "w-6 bg-brand-teal" : "w-2 bg-slate-300"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ResponsiveCarousel;
